import { useState, useEffect, useRef } from 'react';
import Sidebar, { SIDEBAR_W, SIDEBAR_W_COL } from './Sidebar';
import { getNotifications } from '../api';
import { Bell, Sun, Moon, Menu, CheckCheck } from 'lucide-react';
import { useTheme } from '../ThemeContext';
import GlobalSearch from './GlobalSearch';

export const useMobile = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  return isMobile;
};

const notifMeta = (action = '') => {
  if (action.includes('AML_RISK_HIGH'))   return { label: 'High AML Risk',        color: '#DC2626' };
  if (action.includes('AML_RISK_MEDIUM')) return { label: 'Medium AML Risk',      color: '#D97706' };
  if (action.includes('UNSUITABLE'))      return { label: 'Unsuitable Assessment', color: '#DC2626' };
  if (action.includes('BORDERLINE'))      return { label: 'Borderline Assessment', color: '#D97706' };
  if (action.includes('OVERRIDDEN'))      return { label: 'Decision Overridden',   color: '#2563EB' };
  if (action.includes('JAILBREAK'))       return { label: 'Jailbreak Attempt',     color: '#DC2626' };
  if (action.includes('TAMPERED'))        return { label: 'Chain Tampered',        color: '#DC2626' };
  if (action.includes('DORA'))            return { label: 'DORA Incident',         color: '#DC2626' };

  return {
    label: action ? action.replace(/_/g, ' ') : 'Notification',
    color: '#6B7280',
  };
};

const Layout = ({ children }) => {
  const isMobile = useMobile();
  const { isDark, toggle, theme } = useTheme();

  const notifRef = useRef(null);

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [showNotifs, setShowNotifs] = useState(false);
  const [lastSeen, setLastSeen] = useState(() => (
    localStorage.getItem('notif_last_seen') || '1970-01-01'
  ));

  useEffect(() => {
    const fetch = () => {
      getNotifications()
        .then(res => setNotifications(res.data.notifications || []))
        .catch(() => {});
    };

    fetch();

    const interval = setInterval(fetch, 60000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (notifRef.current && !notifRef.current.contains(event.target)) {
        setShowNotifs(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const unreadCount = notifications.filter(n => (
    new Date(n.timestamp) > new Date(lastSeen)
  )).length;

  const handleOpenNotifs = () => {
    setShowNotifs(v => !v);
  };

  const markAllRead = () => {
    const now = new Date().toISOString();
    localStorage.setItem('notif_last_seen', now);
    setLastSeen(now);
  };

  const sidebarWidth = isMobile ? 0 : (collapsed ? SIDEBAR_W_COL : SIDEBAR_W);

  return (
    <div style={{
      display: 'flex',
      minHeight: '100vh',
      background: theme.bgBase,
    }}>
      {/* Mobile overlay */}
      {isMobile && sidebarOpen && (
        <div
          onClick={() => setSidebarOpen(false)}
          style={{
            position: 'fixed',
            inset: 0,
            background: theme.bgOverlay,
            zIndex: 99,
          }}
        />
      )}

      {/* Sidebar */}
      <Sidebar
        onClose={() => setSidebarOpen(false)}
        sidebarOpen={sidebarOpen}
        isMobile={isMobile}
        collapsed={collapsed}
        onCollapse={() => setCollapsed(v => !v)}
      />

      {/* Main */}
      <div style={{
        marginLeft: sidebarWidth,
        flex: 1,
        minWidth: 0,
        overflowX: 'hidden',
        transition: 'margin-left 0.2s ease',
        display: 'flex',
        flexDirection: 'column',
      }}>
        {/* Top header */}
        <header style={{
          position: 'sticky',
          top: 0,
          zIndex: 50,
          background: theme.bgBase,
          borderBottom: `1px solid ${theme.border}`,
          height: 64,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: isMobile ? '0 16px' : '0 28px',
          boxSizing: 'border-box',
        }}>
          {/* Left */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            flex: 1,
            minWidth: 0,
          }}>
            {isMobile && (
              <button
                onClick={() => setSidebarOpen(true)}
                aria-label="Open menu"
                style={{
                  width: 34,
                  height: 34,
                  background: theme.bgBadge,
                  border: `1px solid ${theme.border}`,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: theme.textSecondary,
                  borderRadius: 8,
                  flexShrink: 0,
                }}
              >
                <Menu size={18} />
              </button>
            )}

            {isMobile && (
              <span style={{
                fontSize: 15,
                fontWeight: 700,
                color: theme.textPrimary,
                letterSpacing: 0,
              }}>
                Reg<span style={{ color: '#2563EB' }}>Shield</span>
              </span>
            )}

            {!isMobile && <GlobalSearch />}
          </div>

          {/* Right */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            flexShrink: 0,
          }}>
            {/* Dark mode toggle */}
            <button
              onClick={toggle}
              aria-label="Toggle theme"
              style={{
                width: 34,
                height: 34,
                background: theme.bgBadge,
                border: `1px solid ${theme.border}`,
                borderRadius: 8,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: theme.textSecondary,
                transition: 'all 0.15s',
              }}
              onMouseEnter={e => {
                e.currentTarget.style.background = theme.bgCardHover;
              }}
              onMouseLeave={e => {
                e.currentTarget.style.background = theme.bgBadge;
              }}
            >
              {isDark
                ? <Sun size={16} color="#D97706" />
                : <Moon size={16} color="#6B7280" />
              }
            </button>

            {/* Notification center */}
            <div ref={notifRef} style={{ position: 'relative' }}>
              <button
                onClick={handleOpenNotifs}
                aria-label="Notifications"
                style={{
                  width: 34,
                  height: 34,
                  background: showNotifs ? theme.bgCardHover : theme.bgBadge,
                  border: `1px solid ${showNotifs ? '#2563EB' : theme.border}`,
                  borderRadius: 8,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  position: 'relative',
                  color: showNotifs ? '#2563EB' : theme.textSecondary,
                  transition: 'all 0.15s',
                }}
              >
                <Bell size={16} />

                {unreadCount > 0 && (
                  <span style={{
                    position: 'absolute',
                    top: -5,
                    right: -5,
                    minWidth: 17,
                    height: 17,
                    padding: '0 5px',
                    background: '#DC2626',
                    color: 'white',
                    border: `2px solid ${theme.bgBase}`,
                    borderRadius: 999,
                    fontSize: 10,
                    fontWeight: 700,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxSizing: 'border-box',
                  }}>
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </button>

              {showNotifs && (
                <div style={{
                  position: 'absolute',
                  top: 'calc(100% + 10px)',
                  right: 0,
                  width: isMobile ? 'calc(100vw - 32px)' : 380,
                  maxWidth: 'calc(100vw - 32px)',
                  background: theme.bgCard,
                  borderRadius: 10,
                  boxShadow: theme.shadowLg,
                  border: `1px solid ${theme.border}`,
                  zIndex: 200,
                  overflow: 'hidden',
                }}>
                  {/* Header */}
                  <div style={{
                    padding: '14px 16px',
                    borderBottom: `1px solid ${theme.border}`,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: 12,
                  }}>
                    <div>
                      <p style={{
                        margin: 0,
                        fontSize: 13,
                        fontWeight: 700,
                        color: theme.textPrimary,
                      }}>
                        Notifications
                      </p>
                      <p style={{
                        margin: '3px 0 0',
                        fontSize: 11,
                        color: theme.textTertiary,
                      }}>
                        {notifications.length} alert{notifications.length !== 1 ? 's' : ''}
                        {unreadCount > 0 ? `, ${unreadCount} unread` : ''}
                      </p>
                    </div>

                    {unreadCount > 0 && (
                      <button
                        onClick={markAllRead}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: '#2563EB',
                          fontSize: 12,
                          fontWeight: 600,
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 5,
                          padding: 0,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        <CheckCheck size={13} />
                        Mark read
                      </button>
                    )}
                  </div>

                  {/* Items */}
                  <div style={{ maxHeight: 390, overflowY: 'auto' }}>
                    {notifications.length === 0 ? (
                      <div style={{ padding: '36px 20px', textAlign: 'center' }}>
                        <p style={{
                          fontSize: 13,
                          color: theme.textTertiary,
                          margin: 0,
                        }}>
                          No notifications.
                        </p>
                      </div>
                    ) : notifications.slice(0, 12).map((n, i) => {
                      const meta = notifMeta(n.action);
                      const isUnread = new Date(n.timestamp) > new Date(lastSeen);

                      return (
                        <div
                          key={n.record_id || `${n.action}-${n.timestamp}-${i}`}
                          style={{
                            padding: '13px 16px',
                            borderBottom: i < Math.min(notifications.length, 12) - 1
                              ? `1px solid ${theme.borderLight}`
                              : 'none',
                            background: isUnread ? theme.bgBase : theme.bgCard,
                            display: 'flex',
                            gap: 11,
                            alignItems: 'flex-start',
                          }}
                        >
                          <div style={{
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            background: meta.color,
                            marginTop: 5,
                            flexShrink: 0,
                          }} />

                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              gap: 8,
                              alignItems: 'flex-start',
                            }}>
                              <p style={{
                                margin: 0,
                                fontSize: 12,
                                fontWeight: isUnread ? 700 : 600,
                                color: theme.textPrimary,
                                lineHeight: 1.4,
                              }}>
                                {meta.label}
                              </p>

                              {n.module && (
                                <span style={{
                                  background: theme.bgInput,
                                  color: theme.textTertiary,
                                  border: `1px solid ${theme.border}`,
                                  padding: '2px 7px',
                                  borderRadius: 999,
                                  fontSize: 9,
                                  fontWeight: 600,
                                  whiteSpace: 'nowrap',
                                }}>
                                  {n.module.replace('MODULE_', 'M')}
                                </span>
                              )}
                            </div>

                            {n.entity_id && (
                              <p style={{
                                margin: '4px 0 0',
                                fontSize: 11,
                                color: theme.textSecondary,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}>
                                {n.entity_id}
                              </p>
                            )}

                            <p style={{
                              margin: '5px 0 0',
                              fontSize: 10,
                              color: theme.textTertiary,
                            }}>
                              {new Date(n.timestamp).toLocaleDateString('en-GB', {
                                day: 'numeric',
                                month: 'short',
                                hour: '2-digit',
                                minute: '2-digit',
                              })}
                            </p>
                          </div>

                          {isUnread && (
                            <div style={{
                              width: 6,
                              height: 6,
                              borderRadius: '50%',
                              background: '#2563EB',
                              marginTop: 4,
                              flexShrink: 0,
                            }} />
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {notifications.length > 12 && (
                    <div style={{
                      padding: '11px 16px',
                      borderTop: `1px solid ${theme.border}`,
                      textAlign: 'center',
                      background: theme.bgCard,
                    }}>
                      <p style={{
                        margin: 0,
                        fontSize: 12,
                        color: theme.textTertiary,
                        fontWeight: 500,
                      }}>
                        Showing latest 12 of {notifications.length}
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Page content */}
        <main style={{ flex: 1 }}>
          <div style={{
            padding: isMobile ? '20px 16px' : '28px',
          }}>
            {children}
          </div>
        </main>
      </div>
    </div>
  );
};

export default Layout;
